import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import staioIcon from "@/assets/images/staio-icon.png";
import {
  Building2,
  CalendarCheck,
  BarChart3,
  Sparkles,
  Globe,
  Lock,
  Users,
  ArrowRight,
  CreditCard,
  ClipboardList,
  MessageSquare,
  DoorOpen,
  Shield,
  Clock,
  Wifi,
  ChevronRight,
  Phone,
  CheckCircle2,
  Play,
  Zap,
  TrendingUp,
  BedDouble,
  PiggyBank,
  Bot,
  Workflow,
  Mail,
} from "lucide-react";

const acronymLetters = [
  { letter: "S", word: "Short", color: "from-amber-400 to-orange-500" },
  { letter: "T", word: "Term", color: "from-orange-400 to-red-500" },
  { letter: "A", word: "Advance", color: "from-red-400 to-rose-500" },
  { letter: "I", word: "Integration", color: "from-rose-400 to-pink-500" },
  { letter: "O", word: "Orchestrator", color: "from-pink-400 to-purple-500" },
];

const painPoints = [
  { before: "Spreadsheets for 12 apartments", after: "One dashboard, every unit" },
  { before: "Texting cleaners manually", after: "Auto-dispatched on checkout" },
  { before: "Chasing owner payments", after: "Reconciliation in 2 clicks" },
  { before: "No idea what's actually profitable", after: "Per-unit P&L, real-time" },
];

const operatorProblems = [
  {
    icon: Clock,
    problem: "You're spending Sunday nights updating spreadsheets",
    solution: "Every booking, payment, and cleaning job flows into one system. No copy-pasting between tabs.",
    stat: "4+ hrs/week saved",
  },
  {
    icon: Phone,
    problem: "Your cleaner didn't show up and you found out from a guest",
    solution: "Automated job creation on every checkout. Priority scoring. Push notifications. You know before they do.",
    stat: "Zero missed cleanings",
  },
  {
    icon: CreditCard,
    problem: "You can't tell an owner exactly how much they made last month",
    solution: "Per-apartment financial records, commission splits, expense receipts. Generate owner reports in seconds.",
    stat: "Reports in 10 seconds",
  },
  {
    icon: Globe,
    problem: "You got double-booked because iCal didn't sync",
    solution: "Channel manager connects Airbnb, Booking.com, VRBO, Google — real-time calendar sync, not hourly prayers.",
    stat: "Real-time sync",
  },
];

const capabilities = [
  { icon: Building2, label: "Multi-property dashboard", detail: "Every unit, one screen", expanded: "Centralized operations center showing real-time stats, financial summaries, revenue by apartment, quick actions, and dual business mode support. See everything at a glance — occupancy, pending payments, active reservations, and team workload." },
  { icon: CalendarCheck, label: "Direct booking engine", detail: "Rate plans, taxes, holds", expanded: "Public booking pages per organization with configurable rate plans, availability search, date holds, multi-step checkout, tax calculation, upsell integration, and affiliate tracking. Accept direct bookings without OTA commissions." },
  { icon: Globe, label: "Channel manager", detail: "iCal sync, no double bookings", expanded: "Connect Airbnb, Booking.com, VRBO, and Google Vacation Rentals. Real-time iCal import/export prevents double bookings. Manage all channel connections from one screen per apartment." },
  { icon: ClipboardList, label: "Cleaning automation", detail: "Auto-dispatch, priority queue", expanded: "Guest checkout automatically triggers cleaning job creation with priority scoring. Assign cleaners by availability and rating. Track completion, cleaner earnings per job, and supply costs per apartment type." },
  { icon: CreditCard, label: "Financial tracking", detail: "Revenue, commissions, P&L", expanded: "Comprehensive financial records with commission splits, expense receipts, cash box management (Caja), security deposits, and reconciliation reports. Generate owner invoices and track every dollar in and out." },
  { icon: Lock, label: "Smart lock codes", detail: "Auto-generate per reservation", expanded: "Register smart lock devices per apartment. Automatically generate unique access codes per reservation and revoke them after checkout. Guests get their code in their portal — no key handoffs needed." },
  { icon: Users, label: "Team portals", detail: "6 roles, separate dashboards", expanded: "Admin, Property Manager, Cleaner, Maintenance, Guest, and Affiliate — each with a dedicated portal showing only what they need. Role-based access control ensures data isolation and security." },
  { icon: BarChart3, label: "Reports & forecasting", detail: "Occupancy, revenue, trends", expanded: "Scenario modeling with adjustable inputs for apartments, occupancy, rates, and costs. Historical vs projected revenue timelines, top-performing apartments, monthly breakdowns, and AI-generated investor reports." },
  { icon: Shield, label: "Tax & compliance", detail: "W-9, configurable tax rates", expanded: "Per-rate-plan tax settings with customizable rates and labels. Tax calculated on accommodation and upsell totals. User profiles include W-9 tax forms, ID verification, and bank info for contractor payments." },
  { icon: MessageSquare, label: "Guest experience", detail: "Guides, AI chat, check-in", expanded: "Token-based guest portal with property guides, multi-step check-in flows, bilingual EN/ES support, and an AI-powered chatbot. Guests access everything they need — from directions to lock codes — on their phone." },
  { icon: DoorOpen, label: "Public marketplace", detail: "Direct bookings from the web", expanded: "Browse apartments across organizations with search, filters, and featured listings. Rich detail views with photo galleries, amenities, rate plans, and integrated booking flow. Your own mini-Airbnb." },
  { icon: Sparkles, label: "AI business intelligence", detail: "Insights, anomalies, forecasts", expanded: "Lumi AI agent with morning briefings, anomaly alerts, smart cleaner assignments, natural language search (Cmd+K), and AI-generated owner reports. Context-aware on every page — your always-on operations partner." },
  { icon: Mail, label: "Guest CRM", detail: "Messages, email, platform chat", expanded: "Multi-channel guest communication supporting internal chat, email, SMS, and WhatsApp. Pipeline management with deal stages, contact profiles, conversation threading, tagging, and activity timelines." },
  { icon: Workflow, label: "Automations", detail: "Rules, triggers, auto-actions", expanded: "8 trigger types (reservation, checkin, checkout, payment, ticket, cleaning, maintenance, schedule) and 6 action types (email, notification, SMS, ticket, assign cleaner, update status). Build rules, track run history." },
];

const lumiFeatures = [
  { title: "Morning Briefing", desc: "Today's check-ins, outs, revenue, and what needs your attention — delivered before coffee.", icon: Sparkles },
  { title: "Anomaly Alerts", desc: "Revenue dropped 30% this week? Unpaid invoices stacking up? Lumi catches it, you fix it.", icon: TrendingUp },
  { title: "Smart Assignments", desc: "Which cleaner for this 3BR turnover? Lumi ranks by availability, speed, and rating.", icon: Users },
  { title: "Natural Language Search", desc: "\"How much did Ocean View make in January?\" — just ask. Cmd+K from anywhere.", icon: Zap },
];

const dashboardMockStats = [
  { label: "Active Reservations", value: "24", color: "text-blue-600" },
  { label: "Revenue (MTD)", value: "$18,420", color: "text-green-600" },
  { label: "Check-ins Today", value: "5", color: "text-purple-600" },
  { label: "Occupancy Rate", value: "87%", color: "text-amber-600" },
];

const dashboardMockActivity = [
  { time: "2 min ago", text: "Cleaning completed — Apt 3B (Maria R.)", type: "success" },
  { time: "15 min ago", text: "New booking — Ocean View Suite, Mar 12-15", type: "info" },
  { time: "1 hr ago", text: "Owner report generated — Q4 2025", type: "default" },
  { time: "2 hrs ago", text: "Smart lock code sent — Apt 7A, Guest: James K.", type: "info" },
];

function AnimatedCounter({ end, duration = 2000, prefix = "", suffix = "" }: { end: number; duration?: number; prefix?: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [started, end, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

function DashboardPreview() {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = ["Overview", "Reservations", "Cleaning", "Finances"];

  useEffect(() => {
    const interval = setInterval(() => setActiveTab(prev => (prev + 1) % tabs.length), 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-card border rounded-xl shadow-2xl overflow-hidden" data-testid="dashboard-preview">
      <div className="bg-gradient-to-r from-[#3D2B1F] to-[#4A3428] px-4 py-2.5 flex items-center gap-3">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 text-center">
          <span className="text-xs text-white/60 font-mono">app.staio.io/admin</span>
        </div>
      </div>
      <div className="flex">
        <div className="w-40 bg-[#3D2B1F] hidden sm:flex flex-col py-3 px-2 gap-0.5">
          <div className="flex items-center gap-2 px-2 py-1.5 mb-2">
            <img src={staioIcon} alt="Staio" className="w-5 h-5 rounded" />
            <span className="text-xs font-semibold text-white">Staio</span>
          </div>
          {tabs.map((tab, i) => (
            <button
              key={tab}
              className={`text-left text-[10px] px-2 py-1.5 rounded transition-all ${i === activeTab ? 'bg-white/10 text-white font-medium' : 'text-white/40'}`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="flex-1 p-3 sm:p-4 bg-muted/30 min-h-[220px] sm:min-h-[260px]">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs sm:text-sm font-semibold">{tabs[activeTab]}</h3>
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-md bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                <Sparkles className="w-2.5 h-2.5 text-white" />
              </div>
              <span className="text-[9px] text-muted-foreground">Lumi AI Active</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            {dashboardMockStats.map((s, i) => (
              <div key={i} className="bg-card rounded-md p-2 border shadow-sm">
                <p className="text-[8px] sm:text-[9px] text-muted-foreground">{s.label}</p>
                <p className={`text-sm sm:text-base font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            {dashboardMockActivity.map((a, i) => (
              <div key={i} className={`flex items-start gap-2 text-[9px] sm:text-[10px] p-1.5 rounded-md bg-card border transition-all duration-500 ${i === 0 ? 'ring-1 ring-primary/20' : ''}`}>
                <CheckCircle2 className={`w-3 h-3 shrink-0 mt-0.5 ${a.type === 'success' ? 'text-green-500' : a.type === 'info' ? 'text-blue-500' : 'text-muted-foreground'}`} />
                <div className="min-w-0">
                  <span className="text-muted-foreground">{a.time}</span>
                  <span className="mx-1">—</span>
                  <span>{a.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [activeProblem, setActiveProblem] = useState(0);
  const [expandedCapability, setExpandedCapability] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <nav className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer" data-testid="link-landing-logo">
              <img src={staioIcon} alt="Staio" className="w-7 h-7 rounded-md" />
              <span className="text-lg font-display font-bold">Staio</span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-5 text-sm text-muted-foreground">
            <a href="#how-it-works" className="hover:text-foreground transition-colors" data-testid="link-nav-problems">How It Works</a>
            <a href="#platform" className="hover:text-foreground transition-colors" data-testid="link-nav-capabilities">Platform</a>
            <a href="#lumi" className="hover:text-foreground transition-colors" data-testid="link-nav-lumi">Lumi AI</a>
            <Link href="/marketplace">
              <span className="hover:text-foreground transition-colors cursor-pointer" data-testid="link-nav-marketplace">Marketplace</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/auth">
              <Button variant="ghost" size="sm" data-testid="btn-landing-login">Log In</Button>
            </Link>
            <Link href="/register">
              <Button size="sm" data-testid="btn-landing-signup">
                Sign Up
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-amber-200/20 to-orange-200/10 rounded-full blur-3xl -z-10" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-16 sm:pt-20 sm:pb-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100/60 dark:bg-amber-900/20 mb-5" data-testid="hero-badge">
                <Zap className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-300">The operating system for short-term rentals</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight leading-[1.1]" data-testid="text-hero-title">
                Stop managing properties.{" "}
                <span className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-transparent">
                  Start running a business.
                </span>
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mt-4 sm:mt-5 leading-relaxed max-w-xl" data-testid="text-hero-subtitle">
                Staio replaces the spreadsheets, WhatsApp groups, and guesswork. Bookings, cleaning ops, financials, guest CRM, automation, owner reports — one system, zero duct tape.
              </p>
              <div className="flex flex-wrap gap-3 mt-6 sm:mt-8">
                <Link href="/for-hosts#contact">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/25" data-testid="btn-hero-demo">
                    Request a Demo
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
                <Link href="/marketplace">
                  <Button size="lg" variant="outline" data-testid="btn-hero-explore">
                    <Play className="w-4 h-4 mr-1.5" />
                    See It Live
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-4 mt-6 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> 7-day trial</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> No credit card</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Setup in 10 min</span>
              </div>
            </div>
            <div className="hidden lg:block">
              <DashboardPreview />
            </div>
          </div>
        </div>
      </section>

      <section className="border-b bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
          <div className="flex items-center justify-center gap-1.5 sm:gap-3 mb-6">
            {acronymLetters.map((item, i) => (
              <div key={i} className="text-center group" data-testid={`acronym-${i}`}>
                <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md group-hover:scale-110 transition-transform cursor-default`}>
                  <span className="text-white font-display font-bold text-lg sm:text-2xl">{item.letter}</span>
                </div>
                <p className="text-[9px] sm:text-[10px] font-medium text-muted-foreground mt-1.5">{item.word}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {painPoints.map((p, i) => (
              <div key={i} className="space-y-1.5" data-testid={`pain-point-${i}`}>
                <p className="text-xs sm:text-sm text-muted-foreground line-through decoration-muted-foreground/40">{p.before}</p>
                <p className="text-xs sm:text-sm font-semibold flex items-center gap-1.5">
                  <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" style={{ color: "hsl(24, 70%, 45%)" }} />
                  {p.after}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lg:hidden px-4 py-8 border-b">
        <DashboardPreview />
      </section>

      <section id="how-it-works" className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-10">
            <p className="text-xs sm:text-sm font-medium tracking-wide uppercase mb-2" style={{ color: "hsl(24, 70%, 45%)" }} data-testid="text-problems-label">Real problems, real solutions</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight" data-testid="text-problems-title">
              We built this because we lived it
            </h2>
            <p className="text-muted-foreground mt-3 text-sm sm:text-base leading-relaxed">
              Every feature exists because an operator hit a wall. Here's what actually breaks when you scale past 5 units.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-2">
              {operatorProblems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <button
                    key={i}
                    onClick={() => setActiveProblem(i)}
                    className={`w-full text-left p-3 sm:p-4 rounded-lg border transition-all ${activeProblem === i
                      ? 'border-primary/30 bg-primary/5 shadow-sm'
                      : 'border-transparent hover:bg-muted/50'
                    }`}
                    data-testid={`problem-tab-${i}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${activeProblem === i
                        ? 'bg-amber-100 dark:bg-amber-900/30'
                        : 'bg-muted'
                      }`}>
                        <Icon className={`w-4 h-4 ${activeProblem === i ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`} />
                      </div>
                      <p className="text-xs sm:text-sm font-medium leading-snug line-clamp-2">{item.problem}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="lg:col-span-3">
              <Card className="border bg-card h-full" data-testid="problem-detail">
                <CardContent className="p-5 sm:p-8 flex flex-col justify-center min-h-[220px]">
                  <div key={activeProblem} className="animate-in fade-in slide-in-from-right-4 duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-100/60 dark:bg-amber-900/20 flex items-center justify-center mb-4">
                      {(() => { const Icon = operatorProblems[activeProblem].icon; return <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-amber-700 dark:text-amber-400" />; })()}
                    </div>
                    <p className="text-sm sm:text-base font-semibold text-destructive mb-3">{operatorProblems[activeProblem].problem}</p>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{operatorProblems[activeProblem].solution}</p>
                    <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 dark:bg-green-950/20 w-fit">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                      <span className="text-xs font-semibold text-green-700 dark:text-green-400">{operatorProblems[activeProblem].stat}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section id="platform" className="py-14 sm:py-20 border-y bg-muted/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-8 sm:mb-10">
            <p className="text-xs sm:text-sm font-medium tracking-wide uppercase mb-2" style={{ color: "hsl(24, 70%, 45%)" }} data-testid="text-platform-label">The platform</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight" data-testid="text-capabilities-title">
              <AnimatedCounter end={14} duration={1500} /> modules. One subscription.
            </h2>
            <p className="text-muted-foreground mt-3 text-sm sm:text-base leading-relaxed">
              No more stitching together 6 different tools. Everything your operation needs — from bookings to CRM to automations — built to work together.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5 sm:gap-3">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              const isExpanded = expandedCapability === i;
              return (
                <button
                  key={i}
                  onClick={() => setExpandedCapability(isExpanded ? null : i)}
                  className={`bg-card border rounded-lg p-3 sm:p-4 space-y-1.5 sm:space-y-2 transition-all group text-left ${isExpanded
                    ? 'col-span-2 sm:col-span-3 lg:col-span-4 xl:col-span-5 border-primary/30 shadow-md ring-1 ring-primary/10'
                    : 'hover:shadow-md hover:border-primary/20 cursor-pointer'
                  }`}
                  data-testid={`capability-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-md bg-amber-100/60 dark:bg-amber-900/20 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform ${isExpanded ? 'w-10 h-10' : ''}`}>
                      <Icon className={`text-amber-700 dark:text-amber-400 ${isExpanded ? 'w-5 h-5' : 'w-4 h-4'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`font-semibold leading-tight ${isExpanded ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`}>{cap.label}</p>
                      <p className={`text-muted-foreground mt-0.5 ${isExpanded ? 'text-xs sm:text-sm' : 'text-[10px] sm:text-xs'}`}>{cap.detail}</p>
                      {isExpanded && (
                        <p className="text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed animate-in fade-in slide-in-from-top-2 duration-200">{cap.expanded}</p>
                      )}
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section id="lumi" className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-14 items-start">
            <div className="lg:col-span-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-amber-100/60 to-orange-100/40 dark:from-amber-900/20 dark:to-orange-900/10 mb-3">
                <Bot className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span className="text-[10px] sm:text-xs font-medium text-amber-700 dark:text-amber-300">Built-in AI Agent</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight" data-testid="text-lumi-title">
                Meet Lumi — your AI operations partner
              </h2>
              <p className="text-muted-foreground mt-3 text-sm sm:text-base leading-relaxed">
                Not a chatbot bolted on. Lumi reads your bookings, financials, cleaning data, and occupancy — then tells you what actually matters today. Context-aware on every page you visit.
              </p>
              <div className="mt-6 space-y-3 sm:space-y-4">
                {lumiFeatures.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-md bg-gradient-to-br from-amber-400/20 to-orange-400/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-semibold">{f.title}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 leading-relaxed">{f.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="bg-card border rounded-xl overflow-hidden shadow-lg">
                <div className="bg-gradient-to-r from-amber-50/80 to-orange-50/40 dark:from-amber-950/20 dark:to-orange-950/10 border-b px-4 py-3 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-semibold">Lumi</span>
                  <span className="text-[10px] text-muted-foreground">— context: Dashboard</span>
                  <span className="text-[9px] text-muted-foreground ml-auto">Today, 8:02 AM</span>
                </div>
                <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3 text-sm">
                  <div className="bg-muted/40 rounded-lg p-3 space-y-1.5">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3" /> Morning Briefing
                    </p>
                    <p className="text-xs sm:text-sm"><span className="font-semibold">3 check-ins</span> today, <span className="font-semibold">2 check-outs</span>. Revenue up <span className="font-semibold text-green-600 dark:text-green-400">12%</span> this month.</p>
                    <p className="text-xs text-muted-foreground">1 cleaning job unassigned — Apt 4B, priority high.</p>
                  </div>
                  <div className="bg-amber-50/80 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30 rounded-lg p-3 space-y-1">
                    <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                      <TrendingUp className="w-3 h-3" /> Revenue Insight
                    </p>
                    <p className="text-xs sm:text-sm">"Ocean View" has a <span className="font-semibold">5-day gap</span> next week. Consider dropping the rate by $15/night — similar units booked at that price last month.</p>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-3 space-y-1">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Users className="w-3 h-3" /> Smart Recommendation
                    </p>
                    <p className="text-xs sm:text-sm">Assign <span className="font-semibold">Maria</span> to Job #24 — 12 similar turnovers, 4.9 avg rating, available at 2PM.</p>
                  </div>
                  <div className="border rounded-lg p-2.5 flex items-center gap-2 bg-muted/20">
                    <Wifi className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-muted-foreground text-xs">Ask Lumi anything about your business...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20 border-y bg-muted/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-8 sm:mb-10">
            <p className="text-xs sm:text-sm font-medium tracking-wide uppercase mb-2" style={{ color: "hsl(24, 70%, 45%)" }} data-testid="text-modes-label">Dual business modes</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight" data-testid="text-modes-title">
              Whether you own the units or clean them
            </h2>
            <p className="text-muted-foreground mt-3 text-sm sm:text-base leading-relaxed">
              Staio adapts to your business model. One platform, both modes — switch anytime.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="border bg-card hover:shadow-md transition-shadow" data-testid="card-mode-pm">
              <CardContent className="p-5 sm:p-6">
                <div className="w-10 h-10 rounded-lg bg-amber-100/60 dark:bg-amber-900/20 flex items-center justify-center mb-3">
                  <Building2 className="w-5 h-5 text-amber-700 dark:text-amber-400" />
                </div>
                <h3 className="font-semibold text-sm sm:text-base">Property Management Mode</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed">
                  Track revenue per apartment, split commissions with owners, manage cleaning as an operating expense. Generate investor-grade reports.
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {["Owner reports", "Commission splits", "Booking revenue", "Expense tracking"].map(tag => (
                    <span key={tag} className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{tag}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="border bg-card hover:shadow-md transition-shadow" data-testid="card-mode-services">
              <CardContent className="p-5 sm:p-6">
                <div className="w-10 h-10 rounded-lg bg-amber-100/60 dark:bg-amber-900/20 flex items-center justify-center mb-3">
                  <ClipboardList className="w-5 h-5 text-amber-700 dark:text-amber-400" />
                </div>
                <h3 className="font-semibold text-sm sm:text-base">Services Business Mode</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-2 leading-relaxed">
                  Cleaning, maintenance, and check-ins as revenue lines. Per-cleaner earnings, apartment-type pricing tiers, supply cost tracking, and full P&L.
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {["Cleaner payroll", "Service income", "Supply costs", "Profit margins"].map(tag => (
                    <span key={tag} className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{tag}</span>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 text-center">
            {[
              { value: 6, suffix: "", label: "Dedicated portals", sub: "Admin, PM, Cleaner, Maintenance, Affiliate, Guest" },
              { value: 14, suffix: "+", label: "Platform modules", sub: "Booking to billing, everything built-in" },
              { value: 22, suffix: "", label: "Feature flags", sub: "Toggle capabilities per organization" },
              { value: 2, suffix: "", label: "Languages", sub: "Full bilingual EN/ES support" },
            ].map((stat, i) => (
              <div key={i} className="space-y-1 sm:space-y-2" data-testid={`stat-${i}`}>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold" style={{ color: "hsl(24, 70%, 45%)" }}>
                  <AnimatedCounter end={stat.value} duration={1500} suffix={stat.suffix} />
                </p>
                <p className="text-xs sm:text-sm font-semibold">{stat.label}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t bg-gradient-to-b from-primary/5 to-primary/10 dark:from-primary/3 dark:to-primary/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight" data-testid="text-cta-title">
            Your operation deserves better than spreadsheets
          </h2>
          <p className="text-muted-foreground mt-3 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            Whether you manage 5 apartments or 500, Staio gives you the infrastructure to operate like a real business — not a side hustle.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6 sm:mt-8">
            <Link href="/for-hosts#contact">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/25" data-testid="btn-cta-demo">
                Request a Demo
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link href="/auth">
              <Button size="lg" variant="outline" data-testid="btn-cta-login">
                Log In
              </Button>
            </Link>
          </div>
          <p className="text-[10px] sm:text-xs text-muted-foreground mt-4">No credit card required. Set up in under 10 minutes.</p>
        </div>
      </section>

      <footer className="border-t py-6 sm:py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Building2 className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-sm font-display font-semibold">Staio</span>
              <span className="text-[9px] text-muted-foreground hidden sm:inline">Short Term Advance Integration Orchestrator</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground">
              <Link href="/about"><span className="hover:text-foreground cursor-pointer" data-testid="link-footer-about">About</span></Link>
              <Link href="/for-hosts"><span className="hover:text-foreground cursor-pointer" data-testid="link-footer-hosts">For Hosts</span></Link>
              <Link href="/marketplace"><span className="hover:text-foreground cursor-pointer" data-testid="link-footer-marketplace">Marketplace</span></Link>
              <Link href="/auth"><span className="hover:text-foreground cursor-pointer" data-testid="link-footer-login">Log In</span></Link>
            </div>
            <div className="text-center sm:text-right">
              <a href="mailto:info@staio.io" className="text-[10px] sm:text-xs text-primary hover:underline" data-testid="link-footer-contact">info@staio.io</a>
              <p className="text-[10px] sm:text-xs text-muted-foreground">Rental operations, simplified.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
